import _ from 'underscore';
import { clean } from 'underscore.string';
import { Metrics, segmentCategories } from '@bingads-webui/custom-columns-shared-config';

const COLUMN = 'column';
const OPERATOR = 'operator';
const TEXT = 'text';
export const expressionTokenTypes = { COLUMN, OPERATOR, TEXT };

export const textInputToken = ({
  expandedCategories = {},
  showSuggestions = false,
  value = '',
} = {}) => ({
  expandedCategories,
  showSuggestions,
  type: TEXT,
  value,
});

export const operatorToken = ({
  title,
  value,
}) => ({
  title,
  type: OPERATOR,
  value,
});

export const columnToken = ({
  segments = {},
  title,
  value,
}) => ({
  segments,
  title,
  type: COLUMN,
  value,
});

export const opsTitleToValue = {
  '\u002B': '+',
  '\u2212': '-',
  '\u00D7': '*',
  '\u00F7': '/',
  '(': '(',
  ')': ')',
};
export const opsValueToTitle = _.invert(opsTitleToValue);

/**
 * Modifies the expressionTokens list such that:
 * - the expression begins and ends with an input area
 * - there are input areas between non-input tokens
 * - contents of consecutive inputs are merged into one
 *
 * @param {ExpressionToken[]} expressionTokens list of tokens representing parts of expression
 * @returns {ExpressionToken[]} list of tokens with inputs inserted between non-input types
 */
export const cleanUpExpression = (expressionTokens = []) => {
  const paddedExpr = [];

  for (let i = 0; i < expressionTokens.length; i += 1) {
    const token = expressionTokens[i];
    const prevToken = _.last(paddedExpr) || { type: null };
    const prevType = prevToken.type;

    if (token.type !== TEXT && prevType !== TEXT) {
      paddedExpr.push(textInputToken());
      paddedExpr.push(token);
    } else if (token.type === TEXT && prevType === TEXT) {
      prevToken.value = clean(`${prevToken.value}${token.value}`);
    } else {
      paddedExpr.push(token);
    }
  }

  if ((_.last(paddedExpr) || {}).type !== TEXT) {
    paddedExpr.push(textInputToken());
  }

  return paddedExpr;
};

export const applyPropertyConversions =
  (expressionTokens = [], { permissions: { dynamic = {} } = {} }) => {
    const {
      PartialConversions: IsPartialConversionsEnabled,
      ColumnConversion: IsColumnConversionEnabled,
    } = dynamic;

    if (!IsColumnConversionEnabled) {
      return expressionTokens;
    }

    const transpose = IsPartialConversionsEnabled ? _.identity : _.invert;

    const conversionsMap = transpose({
      'PerformanceMetrics\\Conversions': 'PerformanceMetrics\\ConversionsCredit',
      'PerformanceMetrics\\AllConversions': 'PerformanceMetrics\\AllConversionsCredit',
      'PerformanceMetrics\\ViewThroughConversions': 'PerformanceMetrics\\ViewThroughConversionsCredit',
      'PerformanceMetrics\\CPA': 'PerformanceMetrics\\PartialConversionCPA',
      'PerformanceMetrics\\ConversionRate': 'PerformanceMetrics\\PartialConversionRate',
      'PerformanceMetrics\\AllConversionCPA': 'PerformanceMetrics\\AllPartialConversionCPA',
      'PerformanceMetrics\\AllConversionRate': 'PerformanceMetrics\\AllPartialConversionRate',
      'PerformanceMetrics\\ViewThroughConversionsCPA': 'PerformanceMetrics\\ViewThroughPartialConversionsCPA',
      'PerformanceMetrics\\ViewThroughConversionsRate': 'PerformanceMetrics\\ViewThroughPartialConversionsRate',
    });

    return _.map(expressionTokens, (expressionToken) => {
      if (expressionToken.type === COLUMN) {
        return columnToken({
          ...expressionToken,
          value: _.result(conversionsMap, expressionToken.value, expressionToken.value),
        });
      }
      return expressionToken;
    });
  };

/**
 * Serialize an expression token to the equivalent value expected by MT
 * @param {ExpressionToken} token representing part of an expression
 * @returns {string} serialized form
 */
export const tokenToValue = (token) => {
  let { value } = token;

  if (token.type === COLUMN && token.segments && !_.isEmpty(token.segments.selections)) {
    const category = token.segments.categoryValue;
    const segmentString = _.pluck(token.segments.selections, 'value').join(',');

    value = `${value}[${category}:${segmentString}]`;
  }

  return value;
};

/**
 * Serialize a list of expressionTokens to a string consumable by MT APIs.
 *
 * @param {ExpressionToken[]} expressionTokens list of tokens representing expression
 * @returns {string} serialized expression
 */
export const tokensToString = expressionTokens =>
  clean(_.map(cleanUpExpression(expressionTokens), tokenToValue).join(' '));

/**
 * @param {ExpressionToken[]} expressionTokens the expression state
 * @param {number} index position within expressionTokens
 * @returns {ExpressionToken[]} copy with showSuggestions only enabled @ index
 */
export const showTokenSuggestions = (expressionTokens, index) => _.map(
  expressionTokens,
  (token, i) => _.defaults({
    showSuggestions: index === i,
  }, token)
);

/**
 * @param {ExpressionToken[]} expressionTokens the expression state
 * @param {number} index position within expressionTokens
 * @param {string[]} types valid types to match on
 * @returns {number} index of next match relative to given index
 */
export const nextTokenIndexByTypes = (expressionTokens, index, types) => {
  if (index >= 0 && index < expressionTokens.length) {
    for (let i = index + 1; i < expressionTokens.length; i += 1) {
      if (_.contains(types, expressionTokens[i].type)) {
        return i;
      }
    }
  }

  return index;
};

/**
 * @param {ExpressionToken[]} expressionTokens the expression state
 * @param {number} index position within expressionTokens
 * @param {string[]} types valid types to match on
 * @returns {number} index of previous match relative to given index
 */
export const prevTokenIndexByTypes = (expressionTokens, index, types) => {
  if (index > 0 && index < expressionTokens.length) {
    for (let i = index - 1; i >= 0; i -= 1) {
      if (_.contains(types, expressionTokens[i].type)) {
        return i;
      }
    }
  }

  return index;
};

/**
 * @param {ExpressionToken[]} expressionTokens the expression state
 * @param {number} index position within expressionTokens to preform removal
 * @param {ExpressionToken} token new token instance to be added/inserted
 * @returns {ExpressionToken[]} a copy with the new token added at index
 */
export const addTokenToExpression = (expressionTokens, index, token) => {
  const updatedTokens = [...expressionTokens];

  if (index < 0 || index >= expressionTokens.length - 1) {
    updatedTokens.push(token);
  } else {
    updatedTokens.splice(index, 0, token);
  }

  return cleanUpExpression(updatedTokens);
};

/**
 * Replace the token at index with new tokens (partial splice)
 * @param {ExpressionToken[]} expressionTokens the expression state
 * @param {number} index position within expressionTokens to preform removal
 * @param {ExpressionToken[]} newTokens new tokens to replace the token at index
 * @returns {ExpressionToken[]} a copy with the new tokens replacing the token at index
 */
export const spliceTokens = (expressionTokens, index, newTokens) => {
  if (!_.isArray(newTokens)) {
    throw new Error('newTokens must be an array');
  }

  if (expressionTokens.length < 2) {
    return cleanUpExpression(newTokens);
  }

  const updatedTokens = [...expressionTokens];
  updatedTokens.splice(index, 1, ...newTokens);

  return cleanUpExpression(updatedTokens);
};

/**
 * @param {ExpressionToken[]} expressionTokens the expression state
 * @param {number} index position within expressionTokens to preform removal
 * @returns {ExpressionToken[]} a copy with the value at index removed
 */
export const removeTokenFromExpression = (expressionTokens, index) => {
  if (index < 0 || index > expressionTokens.length - 1) {
    return expressionTokens;
  }

  const updatedTokens = [...expressionTokens];
  updatedTokens.splice(index, 1);

  return cleanUpExpression(updatedTokens);
};

/**
 * @param {ExpressionToken[]} expressionTokens the expression state
 * @param {number} index position within expressionTokens to replace
 * @param {ExpressionToken} newToken replacement token
 * @returns {ExpressionToken[]} a copy with the value at index replaced with newToken
 */
export const replaceTokenWithinExpression = (expressionTokens, index, newToken) =>
  spliceTokens(expressionTokens, index, [newToken]);

const isExpressionMetricNonCapturingRegex =
  /^[A-Z][a-zA-Z]+\\[A-Z][a-zA-Z]+(?:\[[A-Z][a-zA-Z]+:[A-Z][a-zA-Z]+(?:,[A-Z][a-zA-Z]+)*\])?$/;
const parseExpressionMetricRegex =
  /^([A-Z][a-zA-Z]+\\[A-Z][a-zA-Z]+)(?:\[([A-Z][a-zA-Z]+):([A-Z][a-zA-Z]+(?:,[A-Z][a-zA-Z]+)*)\])?$/;
const matchGroups = {
  metricValue: 1,
  segmentCategory: 2,
  segmentSelections: 3,
};
const isExpressionMetric = text => text.match(isExpressionMetricNonCapturingRegex);
const isOperator = text =>
  _.contains(_.union(_.keys(opsValueToTitle), _.values(opsValueToTitle)), text);

const parseExpressionMetric = (metricString, {
  getMetricTitle = _.identity,
  getSegmentCategoryTitle = _.identity,
  getSegmentTitle = _.identity,
}) => {
  const matches = metricString.match(parseExpressionMetricRegex);
  const value = matches[matchGroups.metricValue];
  const segmentCategory = matches[matchGroups.segmentCategory];
  const selectedSegments = matches[matchGroups.segmentSelections];
  const segments = segmentCategory && selectedSegments ? {
    categoryValue: segmentCategory,
    categoryTitle: getSegmentCategoryTitle(segmentCategory),
    selections: _.map(selectedSegments.split(','), segValue => ({
      value: segValue,
      title: getSegmentTitle(`${segmentCategory}:${segValue}`),
    })),
  } : {};

  return columnToken({
    value,
    title: getMetricTitle(value),
    segments,
  });
};

const buildI18nLookups = (i18n) => {
  let getMetricTitle = _.identity;
  let getSegmentCategoryTitle = _.identity;
  let getSegmentTitle = _.identity;

  const metricLookup = {};

  _.each(Metrics, ({ metric, titleKey }) => {
    metricLookup[metric] = i18n.getString(titleKey);
  });
  getMetricTitle = serialized => metricLookup[serialized];

  const segmentLookup = {};
  const segmentCategoryLookup = _.map(segmentCategories, ({
    category,
    titleKey: categoryTitleKey,
    segments,
  }) => {
    _.each(segments, ({ segment, titleKey }) => {
      segmentLookup[`${category}:${segment}`] = i18n.getString(titleKey);
    });

    return [category, i18n.getString(categoryTitleKey)];
  }).reduce((lookup, [key, value]) => _.extend(lookup, { [key]: value }), {});

  getSegmentCategoryTitle = serialized => segmentCategoryLookup[serialized];
  getSegmentTitle = serialized => segmentLookup[serialized];

  return {
    getMetricTitle,
    getSegmentCategoryTitle,
    getSegmentTitle,
  };
};

/**
 * @param {string[]} expressionArray list of strings representing serialized expression
 * @param {object} props configuration
 * @returns {ExpressionToken[]} equivalent ExpressionToken representation
 */
export const tokenize = (expressionArray, {
  i18n,
} = { i18n: { getString: _.identity } }) => {
  if (!_.isArray(expressionArray) || _.isEmpty(expressionArray)) {
    return [textInputToken()];
  }

  let i18nLookups; // only generate i18n lookup tables if/when first needed

  return cleanUpExpression(_.compact(_.map(expressionArray, (exprPart) => {
    if (_.isString(exprPart) && !_.isEmpty(exprPart)) {
      if (isOperator(exprPart)) {
        const value = opsTitleToValue[exprPart] ? opsTitleToValue[exprPart] : exprPart;
        return operatorToken({ value, title: opsValueToTitle[value] });
      } else if (isExpressionMetric(exprPart)) {
        if (!i18nLookups) {
          i18nLookups = buildI18nLookups(i18n);
        }

        return parseExpressionMetric(exprPart, i18nLookups);
      }
    }

    return textInputToken({ value: exprPart });
  })));
};
